<template>
  <div class="bg-section">
    <v-card color="section" flat>
      <v-card-title class="text-h6">Product Types</v-card-title>
      <v-card-text>
        <v-col class="pa-0">
          <v-card
            v-for="template in productTemplates"
            :key="template.title"
            class="mb-1 rounded border-thin d-flex domain-item align-center"
            :class="{
              'flex-row': mdAndUp,
              'flex-column': !mdAndUp
            }"
            flat
            :data-testid="`template-${template.title}`"
            @click="updateProducts(template)"
          >
            <component
              :is="template.icon"
              class="flex-grow-0 flex-shrink-0 text-center ma-3 ml-6 domain-icon"
              :class="{
                'lg-domain-icon': mdAndUp,
                'sm-domain-icon': !mdAndUp
              }"
            />
            <div class="pa-3 flex-grow-1">
              <v-card-title class="pl-0 pt-0 d-flex align-center justify-start">
                {{ template.title }}
                <div class="checkbox-width">
                  <v-radio-group
                    :model-value="domainTemplate"
                    color="accent"
                    hide-details
                    @update:model-value="updateProducts(template)"
                  >
                    <v-radio
                      :value="template.title"
                      class="ma-0 ml-2"
                      density="compact"
                    />
                  </v-radio-group>
                </div>
                <v-spacer />
                <active-save-indicator
                  class="ml-2"
                  :controller="savingBuffer[template.title].controller.value"
                />
              </v-card-title>
              <div>
                <p class="text-body-1 mb-1">
                  {{ template.description }}
                </p>

                <v-chip-group
                  v-if="template.editable"
                  v-model="enabledProductTypes"
                  multiple
                  mandatory
                  :selected-class="
                    domainTemplate == template.title ? 'bg-primary' : null
                  "
                  class="ga-1"
                  column
                >
                  <v-chip
                    v-for="product in template.products"
                    :key="product"
                    :value="product"
                    :data-testid="`custom-${toProductText(product)}`"
                    class="rounded-sm"
                    @click.stop
                  >
                    {{ toProductText(product) }}
                    <v-icon
                      v-if="domainTemplate == template.title"
                      class="ml-1"
                      :icon="
                        enabledProductTypes.includes(product)
                          ? mdiCheckboxMarked
                          : mdiCheckboxBlankOutline
                      "
                    />
                  </v-chip>
                </v-chip-group>
                <v-row v-else class="ma-0 ga-1">
                  <v-chip
                    v-for="product in template.products"
                    :key="product"
                    :value="product"
                    :class="
                      domainTemplate == template.title ? 'bg-primary' : null
                    "
                    class="rounded-sm cursor-not-allowed"
                    @click.stop
                  >
                    {{ toProductText(product) }}
                  </v-chip>
                </v-row>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
import ActiveSaveIndicator from "@/components/shared/active-save/ActiveSaveIndicator.vue";
import { storeToRefs } from "pinia";
import { useApprovedDomainView } from "@/stores/approved-domain-view";
import { computed, markRaw, ref } from "vue";
import SailboatIcon from "@/components/approved-domain/approved-domain-icons/SailboatIcon.vue";
import UmbrellaIcon from "@/components/approved-domain/approved-domain-icons/UmbrellaIcon.vue";
import BeachChairIcon from "@/components/approved-domain/approved-domain-icons/BeachChairIcon.vue";
import ColorTreeIcon from "@/components/approved-domain/approved-domain-icons/ColorTreeIcon.vue";
import BenchIcon from "@/components/approved-domain/approved-domain-icons/BenchIcon.vue";
import ShieldIcon from "@/components/approved-domain/approved-domain-icons/ShieldIcon.vue";
import CogIcon from "@/components/approved-domain/approved-domain-icons/CogIcon.vue";
import CheckboxCircleIcon from "@/components/approved-domain/approved-domain-icons/CheckboxCircleIcon.vue";
import { PRODUCT_TYPE, PRODUCT_TYPES } from "@/models/ApprovedDomain";
import { mdiCheckboxBlankOutline, mdiCheckboxMarked } from "@mdi/js";
import { useActiveSave } from "@/composables/active-save.composable";
import { useDisplay } from "vuetify";

const { mdAndUp } = useDisplay();
const approvedDomain = useApprovedDomainView();

const { productTypes } = storeToRefs(approvedDomain);

const toProductText = value => PRODUCT_TYPES.find(v => v.value === value).title;

const productTemplates = [
  {
    icon: markRaw(CheckboxCircleIcon),
    title: "All",
    description: "Recommended for most users. All product types are available.",
    products: PRODUCT_TYPES.map(a => a.value)
  },
  {
    icon: markRaw(UmbrellaIcon),
    title: "Accidental Death",
    description:
      "Accidental Death mode creates a short application process for guaranteed acceptance accidental death products.",
    products: [PRODUCT_TYPE.ACCIDENTAL_DEATH]
  },
  {
    icon: markRaw(BeachChairIcon),
    title: "Annuity",
    description:
      "Annuity mode focuses on fixed and indexed annuity solutions designed to provide guaranteed income for life, helping agents quickly find the most competitive products that offer their clients guaranteed income for life and protection from market volatility.",
    products: [PRODUCT_TYPE.ANNUITY_INCOME_RIDER]
  },
  {
    icon: markRaw(ColorTreeIcon),
    title: "Final Expense",
    description:
      "Final Expense mode streamlines the quoting and application process for final expense sales, making it easier and faster for agents to help clients find the right coverage for burial expenses.",
    products: [PRODUCT_TYPE.FINAL_EXPENSE]
  },
  {
    icon: markRaw(BenchIcon),
    title: "Long Term Care",
    description:
      "Long Term Care mode compares traditional long term care, linked benefits, and life with long term care insurance, making it easier and faster for agents to help clients find the right coverage for long term care needs.",
    products: [
      PRODUCT_TYPE.LINKED_BENEFIT,
      PRODUCT_TYPE.LONG_TERM_CARE,
      PRODUCT_TYPE.PROTECTION_IUL,
      PRODUCT_TYPE.PROTECTION_VUL,
      PRODUCT_TYPE.UNIVERSAL_LIFE,
      PRODUCT_TYPE.WHOLE_LIFE
    ]
  },
  {
    icon: markRaw(ShieldIcon),
    title: "Mortgage Protection",
    description:
      "Mortgage Protection mode focuses exclusively on term life insurance.",
    products: [PRODUCT_TYPE.TERM]
  },
  {
    icon: markRaw(SailboatIcon),
    title: "Retirement Focus",
    description:
      "Retirement Focus mode focuses on life insurance solutions designed to protect and enhance your client's retirement, helping agents quickly find coverage that safeguards their financial future while building long-term cash value.",
    products: [PRODUCT_TYPE.ACCUMULATION_IUL, PRODUCT_TYPE.WHOLE_LIFE]
  },
  {
    icon: markRaw(CogIcon),
    title: "Custom",
    description:
      "Custom mode allows you to select any combination of product types to create a tailored quoting experience that matches your specific needs.",
    editable: true,
    products: PRODUCT_TYPES.map(a => a.value)
  }
];
const enabledProductTypes = computed({
  get() {
    return Object.keys(productTypes.value).filter(k => productTypes.value[k]);
  },
  set(v) {
    for (const key of Object.keys(productTypes.value)) {
      productTypes.value[key] = v.includes(key);
    }
    saveAttribute(domainTemplate.value, 300);
  }
});

const domainTemplate = ref(null);

function updateProducts(config) {
  if (domainTemplate.value === config.title) return;
  domainTemplate.value = config.title;
  enabledProductTypes.value = config.products;
}

function determineInitialDomainTemplate() {
  const supportedProductTypes = Object.values(PRODUCT_TYPE).reduce(
    (acc, curr) => ({ ...acc, [curr]: true }),
    {}
  );
  const sortedProductTypes = enabledProductTypes.value
    .filter(a => Boolean(supportedProductTypes[a]))
    .toSorted((a, b) => a.localeCompare(b));
  return (
    productTemplates.find(template => {
      const sortedProducts = template.products.toSorted((a, b) =>
        a.localeCompare(b)
      );

      return sortedProductTypes.toString() === sortedProducts.toString();
    })?.title || "Custom"
  );
}

const savingBuffer = productTemplates.reduce(
  (acc, curr) => ({ ...acc, [curr.title]: useActiveSave() }),
  {}
);

function saveAttribute(attribute, timeout = 300) {
  savingBuffer[attribute].debounceUpdate(
    () => approvedDomain.updateApprovedDomain("productTypes"),
    timeout
  );
}

domainTemplate.value = determineInitialDomainTemplate();
</script>

<style lang="scss">
.domain-icon {
  filter: drop-shadow(0px 0px 2px rgb(0 0 0 / 0.3));
}

.lg-domain-icon {
  height: 125px;
  width: 125px;
}

.sm-domain-icon {
  height: 100px;
  width: 100px;
}

.cursor-not-allowed {
  cursor: not-allowed;
}
</style>
